import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { themeApi, assetUrl } from '../utils/api';

const ThemeContext = createContext(null);

const DEFAULT_SETTINGS = {
  '--navy-deep': '#0d1b2a', '--navy-mid': '#1a3c5e', '--navy-light': '#1b2838', '--navy-card': '#162231',
  '--amber': '#e8a838', '--amber-light': '#f0c26a', '--amber-dim': '#c48a2a',
  '--green': '#3adc8a', '--green-dim': '#2aad6b', '--red': '#e85d5d', '--red-dim': '#c44040',
  '--blue': '#4a9eff', '--blue-dim': '#3a7fcc', '--warning': '#f5a623',
  '--text-primary': '#f0f2f5', '--text-secondary': '#8a9bb5', '--text-muted': '#5c6d83',
  '--border': '#2a3f56', '--surface': '#1e2d3d', '--surface-hover': '#243548',
  '--radius-sm': '6px', '--radius-md': '10px', '--radius-lg': '16px', '--radius-xl': '24px',
  '--font-display': "'Oswald', sans-serif", '--font-body': "'IBM Plex Sans', sans-serif"
};

const GOOGLE_FONTS_MAP = {
  'Oswald': 'Oswald:wght@400;500;600;700',
  'Montserrat': 'Montserrat:wght@400;500;600;700',
  'Bebas Neue': 'Bebas+Neue',
  'Roboto Condensed': 'Roboto+Condensed:wght@400;500;600;700',
  'IBM Plex Sans': 'IBM+Plex+Sans:wght@300;400;500;600',
  'Inter': 'Inter:wght@300;400;500;600',
  'Open Sans': 'Open+Sans:wght@300;400;500;600',
  'Lato': 'Lato:wght@300;400;700'
};

function applyCSS(settings) {
  const root = document.documentElement;
  Object.entries(settings).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

function injectFontLinks(settings) {
  // Remove old injected links
  document.querySelectorAll('link[data-theme-font]').forEach(el => el.remove());

  const fonts = new Set();
  const displayFont = (settings['--font-display'] || '').replace(/'/g, '').split(',')[0].trim();
  const bodyFont = (settings['--font-body'] || '').replace(/'/g, '').split(',')[0].trim();

  if (displayFont && GOOGLE_FONTS_MAP[displayFont]) fonts.add(GOOGLE_FONTS_MAP[displayFont]);
  if (bodyFont && GOOGLE_FONTS_MAP[bodyFont]) fonts.add(GOOGLE_FONTS_MAP[bodyFont]);

  if (fonts.size > 0) {
    const url = `https://fonts.googleapis.com/css2?${[...fonts].map(f => `family=${f}`).join('&')}&display=swap`;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.setAttribute('data-theme-font', 'true');
    document.head.appendChild(link);
  }
}

export function ThemeProvider({ children }) {
  const [siteName, setSiteName] = useState('DerbyScores');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoDarkUrl, setLogoDarkUrl] = useState(null);
  const [siteUrl, setSiteUrl] = useState('');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);

  const loadTheme = useCallback(async () => {
    try {
      const { data } = await themeApi.getTheme();
      const d = data.data;
      setSiteName(d.site_name);
      setLogoUrl(assetUrl(d.logo_url));
      setLogoDarkUrl(d.logo_dark_url ? assetUrl(d.logo_dark_url) : null);
      setSiteUrl(d.site_url || '');
      setSettings(d.settings);
      setOverrides(d.overrides);
      applyCSS(d.settings);
      injectFontLinks(d.settings);
    } catch (err) {
      console.error('Failed to load theme:', err);
      applyCSS(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTheme(); }, [loadTheme]);

  // Live preview: apply CSS vars without saving
  const previewSettings = useCallback((newSettings) => {
    applyCSS(newSettings);
    injectFontLinks(newSettings);
  }, []);

  // Revert preview back to saved state
  const revertPreview = useCallback(() => {
    applyCSS(settings);
    injectFontLinks(settings);
  }, [settings]);

  return (
    <ThemeContext.Provider value={{
      siteName, logoUrl, logoDarkUrl, siteUrl, settings, overrides, loading,
      loadTheme, previewSettings, revertPreview,
      DEFAULT_SETTINGS
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
